/**
 * Pure detector for the three composer popovers (slash, mention, emoji
 * shortcode). Inspects either the raw text (slash command) or the
 * pre-cursor text from ProseMirror (mention + emoji shortcode) and
 * returns a discriminated union the caller pattern-matches on.
 *
 * Extracted from `MessageInput.handleEditorChange` so the detection
 * rules can be tested without booting Tiptap.
 */

export type PopoverTrigger =
  | { kind: "slash"; filter: string }
  | { kind: "mention"; filter: string }
  | { kind: "emoji"; filter: string }
  | null;

/**
 * Detect a `/<command>` slash command. Only fires when the editor text
 * starts with `/` and no whitespace has been typed yet (so the user is
 * still building the command).
 */
export function detectSlashCommand(text: string): PopoverTrigger {
  if (!text.startsWith("/")) return null;
  if (text.slice(1).includes(" ")) return null;
  const filter = text.slice(1).split(/\s/)[0] ?? "";
  return { kind: "slash", filter };
}

/**
 * Detect an `@<filter>` mention. The argument is the text between the
 * start of the current ProseMirror block and the cursor — caller is
 * expected to use `textBetween(0, parentOffset, undefined, '￼')`
 * so atom nodes (already-inserted mentions) are masked out.
 *
 * Fires only when the `@` is at start-of-block, after a space, or after
 * a newline — and the filter so far contains no spaces or newlines.
 */
export function detectMentionTrigger(textBeforeCursor: string): PopoverTrigger {
  const atIndex = textBeforeCursor.lastIndexOf("@");
  if (atIndex === -1) return null;
  if (
    atIndex !== 0 &&
    textBeforeCursor[atIndex - 1] !== " " &&
    textBeforeCursor[atIndex - 1] !== "\n"
  ) {
    return null;
  }
  const filter = textBeforeCursor.slice(atIndex + 1);
  if (filter.includes(" ") || filter.includes("\n")) return null;
  return { kind: "mention", filter };
}

/**
 * Detect a `:<shortcode>` emoji-shortcode trigger. Same boundary rules
 * as mentions plus an extra guard against a second `:` (which would
 * indicate the user already finished a shortcode like `:smile:`).
 */
export function detectEmojiShortcodeTrigger(
  textBeforeCursor: string,
): PopoverTrigger {
  const colonIndex = textBeforeCursor.lastIndexOf(":");
  if (colonIndex === -1) return null;
  if (
    colonIndex !== 0 &&
    textBeforeCursor[colonIndex - 1] !== " " &&
    textBeforeCursor[colonIndex - 1] !== "\n"
  ) {
    return null;
  }
  const filter = textBeforeCursor.slice(colonIndex + 1);
  if (filter.includes(" ") || filter.includes("\n") || filter.includes(":")) {
    return null;
  }
  return { kind: "emoji", filter };
}
